import { useCallback, useState } from 'react'
import { lumi } from '@/lib/lumi'
import type { ContractType, JobCategory } from '@/data/jobs'
import {
  emailRecruiter_NewAEApplication,
  emailRecruiter_NewStandardApplication,
  emailCandidate_AEAccepted,
  emailCandidate_AERejected,
  emailCandidate_AEUnderReview,
  emailCandidate_StandardUnderReview,
  emailCandidate_StandardAccepted,
  emailCandidate_StandardRejected,
  emailCandidate_InterviewInvite,
} from '@/lib/emails'

// ─── Types ───────────────────────────────────────────────────────────────────

export type ApplicationStatus =
  | 'nouveau'
  | 'en_examen'
  | 'entretien'
  | 'retenu'
  | 'refuse'

export const APPLICATION_STATUS_LABELS: Record<ApplicationStatus, string> = {
  nouveau: 'Nouveau',
  en_examen: 'En examen',
  entretien: 'Entretien',
  retenu: 'Retenu',
  refuse: 'Refusé',
}

export interface ApplicationDossier {
  category: JobCategory
  diplomeType?: string
  files?: Record<string, string>
  updatedAt?: string
}

export interface Application {
  _id: string
  jobId: string
  jobTitle: string
  jobEmployer?: string
  jobCategory: JobCategory
  contractType: ContractType
  fullName: string
  email: string
  phone?: string
  message?: string
  cvFileName?: string
  cvUrl?: string
  proposedRate?: number
  dossier?: ApplicationDossier
  candidateId?: string
  recruiterId?: string
  status: ApplicationStatus
  creator: string
  createdAt: string
  updatedAt: string
}

export type NewApplication = Omit<
  Application,
  '_id' | 'creator' | 'createdAt' | 'updatedAt' | 'status'
> & {
  status?: ApplicationStatus
}

// ─── Helper : récupérer l'email du recruteur depuis recruiter_jobs ───────────

/**
 * Tente de retrouver l'email du recruteur propriétaire d'une offre.
 * Best-effort : retourne null si non trouvable.
 */
async function fetchRecruiterEmailForJob(jobId: string): Promise<{
  email: string | null
  fullName?: string
}> {
  try {
    // 1. Si l'offre est en base recruiter_jobs (id = _id), on récupère le doc
    const job = await lumi.entities.recruiter_jobs.get?.(jobId).catch(() => null)
    if (job) {
      const j = job as Record<string, unknown>
      const email = (j.creatorEmail as string | undefined) || null
      const fullName = (j.creatorName as string | undefined) || (j.employer as string | undefined)
      if (email) return { email, fullName }

      // Fallback : si pas de creatorEmail, chercher l'utilisateur par creator
      const creator = j.creator as string | undefined
      if (creator) {
        const userEmail = await fetchUserEmailById(creator)
        if (userEmail) return { email: userEmail, fullName }
      }
    }
  } catch (e) {
    console.warn('[email] fetchRecruiterEmailForJob: lookup get failed', e)
  }

  // 2. Sinon, on fait un list filtré sur _id (cas où get n'est pas exposé)
  try {
    const { list } = await lumi.entities.recruiter_jobs.list({
      filter: { _id: jobId },
      limit: 1,
    })
    if (list && list[0]) {
      const j = list[0] as Record<string, unknown>
      const email = (j.creatorEmail as string | undefined) || null
      const fullName = (j.creatorName as string | undefined) || (j.employer as string | undefined)
      if (email) return { email, fullName }
      const creator = j.creator as string | undefined
      if (creator) {
        const userEmail = await fetchUserEmailById(creator)
        if (userEmail) return { email: userEmail, fullName }
      }
    }
  } catch (e) {
    console.warn('[email] fetchRecruiterEmailForJob: list failed', e)
  }

  return { email: null }
}

/**
 * Tente de retrouver l'email d'un utilisateur Lumi à partir de son userId.
 * Lumi expose la collection sub_project_user_account ; on tente plusieurs voies.
 */
async function fetchUserEmailById(userId: string): Promise<string | null> {
  try {
    const userEntities = (lumi.entities as Record<string, unknown>).sub_project_user_account as
      | { list: (q: Record<string, unknown>) => Promise<{ list: unknown[] }> }
      | undefined
    if (userEntities?.list) {
      const { list } = await userEntities.list({ filter: { user_id: userId }, limit: 1 })
      if (list && list[0]) {
        const u = list[0] as Record<string, unknown>
        return (u.email as string | undefined) || null
      }
    }
  } catch (e) {
    console.warn('[email] fetchUserEmailById failed', e)
  }
  return null
}

/**
 * Tente de retrouver l'email d'un candidat à partir de son creator (userId)
 * — en se basant sur la table user account, sinon sur les applications existantes.
 */
async function fetchCandidateEmailById(creator: string): Promise<string | null> {
  // 1. Via la collection users
  const direct = await fetchUserEmailById(creator)
  if (direct) return direct
  // 2. Via la dernière candidature connue (fallback)
  try {
    const { list } = await lumi.entities.applications.list({
      filter: { creator },
      sort: { createdAt: -1 },
      limit: 1,
    })
    if (list && list[0]) {
      const a = list[0] as Record<string, unknown>
      return (a.email as string | undefined) || null
    }
  } catch {
    // ignore
  }
  return null
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useApplications() {
  const [applications, setApplications] = useState<Application[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchApplications = useCallback(
    async (filter?: Record<string, unknown>) => {
      setLoading(true)
      setError(null)
      try {
        const { list } = await lumi.entities.applications.list({
          filter: filter ?? {},
          sort: { createdAt: -1 },
        })
        setApplications(list as Application[])
        return list as Application[]
      } catch (e) {
        console.error('applications.list failed:', e)
        setError('Impossible de charger les candidatures.')
        return []
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const fetchByCandidate = useCallback(
    async (candidateUserId: string) => {
      return fetchApplications({ creator: candidateUserId })
    },
    [fetchApplications]
  )

  const fetchByRecruiter = useCallback(
    async (recruiterUserId: string) => {
      return fetchApplications({ recruiterId: recruiterUserId })
    },
    [fetchApplications]
  )

  /**
   * Crée une candidature et notifie le recruteur par email.
   * Email 1 (AE) ou Email 2 (autre contrat) — du candidat au recruteur.
   * Best-effort : l'email ne bloque jamais le retour de la création.
   */
  const createApplication = useCallback(
    async (data: NewApplication, creator: string) => {
      const now = new Date().toISOString()
      const payload: Record<string, unknown> = {
        jobId: data.jobId,
        jobTitle: data.jobTitle,
        jobCategory: data.jobCategory,
        contractType: data.contractType,
        fullName: data.fullName,
        email: data.email,
        status: data.status || 'nouveau',
        creator,
        createdAt: now,
        updatedAt: now,
      }
      if (data.jobEmployer) payload.jobEmployer = data.jobEmployer
      if (data.phone) payload.phone = data.phone
      if (data.message) payload.message = data.message
      if (data.cvFileName) payload.cvFileName = data.cvFileName
      if (data.cvUrl) payload.cvUrl = data.cvUrl
      if (typeof data.proposedRate === 'number' && !isNaN(data.proposedRate)) {
        payload.proposedRate = data.proposedRate
      }
      if (data.dossier) payload.dossier = data.dossier
      if (data.candidateId) payload.candidateId = data.candidateId
      if (data.recruiterId) payload.recruiterId = data.recruiterId

      const created = await lumi.entities.applications.create(payload)
      console.log('applications.create OK', created)

      // ── Notifier le recruteur (best-effort) ──────────────────────────────
      void (async () => {
        try {
          const { email: recruiterEmail, fullName: recruiterName } =
            await fetchRecruiterEmailForJob(data.jobId)
          if (!recruiterEmail) {
            console.warn(
              '[email] Aucun email recruteur trouvé pour jobId=',
              data.jobId,
              '— notification ignorée.'
            )
            return
          }
          if (data.contractType === 'auto-entrepreneur') {
            await emailRecruiter_NewAEApplication({
              recruiterEmail,
              recruiterName,
              candidateName: data.fullName,
              candidateEmail: data.email,
              candidatePhone: data.phone,
              jobTitle: data.jobTitle,
              jobEmployer: data.jobEmployer || 'Employeur',
              proposedRate: data.proposedRate,
            })
          } else {
            await emailRecruiter_NewStandardApplication({
              recruiterEmail,
              recruiterName,
              candidateName: data.fullName,
              candidateEmail: data.email,
              candidatePhone: data.phone,
              jobTitle: data.jobTitle,
              jobEmployer: data.jobEmployer || 'Employeur',
              contractType: data.contractType,
            })
          }
        } catch (e) {
          console.warn('[email] Notification recruteur échouée:', e)
        }
      })()

      return created as Application
    },
    []
  )

  /**
   * Met à jour le statut d'une candidature et envoie l'email correspondant
   * au candidat selon le statut + contractType.
   * Emails 3, 4, 5, 6 — du recruteur au candidat.
   *
   * STRATÉGIE : On lit le document complet via applications.list({ creator }) AVANT
   * d'appeler update(), car lumi.entities.*.update() retourne uniquement le patch
   * (pas le doc complet). On ne peut pas non plus filtrer par _id dans list().
   * On passe donc le document déjà connu (depuis le state local du Backoffice)
   * via le paramètre optionnel `knownApp`.
   */
  const updateStatus = useCallback(
    async (id: string, status: ApplicationStatus, knownApp?: Application): Promise<{ emailSent: boolean; emailReason?: string }> => {
      console.log('[updateStatus] START', { id, status, hasKnownApp: !!knownApp, knownAppEmail: knownApp?.email })
      if (typeof id !== 'string' || !id) {
        throw new Error('Invalid application id')
      }

      // ── 1. Faire l'update en base IMMÉDIATEMENT et récupérer le doc complet ─
      // Le PUT Lumi renvoie le document complet dans response.data — on l'utilise.
      let updatedDoc: Application | null = null
      try {
        const result = await lumi.entities.applications.update(id, {
          status,
          updatedAt: new Date().toISOString(),
        })
        console.log('[updateStatus] applications.update OK', { id, status, result })
        // Le SDK peut renvoyer le doc complet (selon les logs SDK observés) OU juste le patch
        if (result && typeof result === 'object' && 'email' in result) {
          updatedDoc = result as unknown as Application
          console.log('[updateStatus] Doc complet récupéré depuis update()', updatedDoc.email)
        }
      } catch (e) {
        console.error('[updateStatus] applications.update FAILED', e)
        throw e
      }

      // ── 2. Choisir la source de vérité pour les données candidat ──────────
      // Priorité : 1) doc renvoyé par update(), 2) knownApp passé en paramètre,
      // 3) fallback list par jobId, 4) fallback list par creator.
      let app: Application | null = updatedDoc && updatedDoc.email ? updatedDoc : null

      if (!app && knownApp && knownApp.email) {
        console.log('[updateStatus] Source: knownApp', knownApp.email)
        app = knownApp
      }

      if (!app && knownApp?.jobId) {
        try {
          console.log('[updateStatus] Fallback fetch par jobId', knownApp.jobId)
          const { list } = await lumi.entities.applications.list({
            filter: { jobId: knownApp.jobId },
            sort: { createdAt: -1 },
          })
          const found = (list as Application[]).find(
            (a) => String((a._id as unknown as { $oid?: string })?.$oid ?? a._id) === id
          )
          if (found?.email) {
            app = { ...found, _id: id }
            console.log('[updateStatus] Fallback jobId: doc trouvé', app.email)
          }
        } catch (e) {
          console.warn('[updateStatus] Fallback jobId échoué:', e)
        }
      }

      if (!app && knownApp?.creator) {
        try {
          console.log('[updateStatus] Fallback fetch par creator', knownApp.creator)
          const { list } = await lumi.entities.applications.list({
            filter: { creator: knownApp.creator },
            sort: { createdAt: -1 },
            limit: 20,
          })
          const found = (list as Application[]).find(
            (a) => String((a._id as unknown as { $oid?: string })?.$oid ?? a._id) === id
          )
          if (found?.email) {
            app = { ...found, _id: id }
            console.log('[updateStatus] Fallback creator: doc trouvé', app.email)
          }
        } catch (e) {
          console.warn('[updateStatus] Fallback creator échoué:', e)
        }
      }

      if (!app || !app.email) {
        const reason = `Aucun document candidat exploitable trouvé pour id=${id}`
        console.warn('[updateStatus] EMAIL ANNULÉ —', reason)
        return { emailSent: false, emailReason: reason }
      }

      // ── 3. Envoyer l'email — AWAITED dans le flux principal ───────────────
      // (plus de void(async)() détaché qui peut être interrompu par React)
      const candidateEmail = app.email
      const candidateName = app.fullName || 'Candidat(e)'
      const jobTitle = app.jobTitle || 'votre offre'
      const jobEmployer = app.jobEmployer || 'Employeur'
      const contractType = app.contractType
      const isAE = contractType === 'auto-entrepreneur'

      console.log('[updateStatus] ENVOI EMAIL →', { status, contractType, candidateEmail, isAE })

      try {
        if (status === 'retenu' && isAE) {
          await emailCandidate_AEAccepted({ candidateEmail, candidateName, jobTitle, jobEmployer })
        } else if (status === 'refuse' && isAE) {
          await emailCandidate_AERejected({ candidateEmail, candidateName, jobTitle, jobEmployer })
        } else if (status === 'en_examen' && isAE) {
          await emailCandidate_AEUnderReview({ candidateEmail, candidateName, jobTitle, jobEmployer })
        } else if (status === 'en_examen' && !isAE) {
          await emailCandidate_StandardUnderReview({ candidateEmail, candidateName, jobTitle, jobEmployer, contractType })
        } else if (status === 'retenu' && !isAE) {
          await emailCandidate_StandardAccepted({ candidateEmail, candidateName, jobTitle, jobEmployer, contractType })
        } else if (status === 'refuse' && !isAE) {
          await emailCandidate_StandardRejected({ candidateEmail, candidateName, jobTitle, jobEmployer, contractType })
        } else if (status === 'entretien') {
          await emailCandidate_InterviewInvite({ candidateEmail, candidateName, jobTitle, jobEmployer, contractType })
        } else {
          console.log('[updateStatus] Statut', status, '— pas d\'email défini')
          return { emailSent: false, emailReason: `Aucun template défini pour le statut '${status}'` }
        }
        console.log('[updateStatus] EMAIL ENVOYÉ ✓ à', candidateEmail)
        return { emailSent: true }
      } catch (e) {
        const reason = e instanceof Error ? e.message : String(e)
        console.error('[updateStatus] ENVOI EMAIL ÉCHOUÉ', reason, e)
        return { emailSent: false, emailReason: reason }
      }
    },
    []
  )

  return {
    applications,
    loading,
    error,
    fetchApplications,
    fetchByCandidate,
    fetchByRecruiter,
    createApplication,
    updateStatus,
    setApplications,
  }
}
